import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

interface getRootFileZoneProps {
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onClick: () => void;
}

interface getInputFileZoneProps {
  type: string;
  multiple: boolean;
  accept: string;
  max: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface FileState {
  id: string;
  file: File;
  previewUrl?: string; // Preview URL for images and videos
  videoPreviewUrl?: string; // Separate URL for video playback
  state: {
    status:
    | "pending"
    | "uploading"
    | "paused"
    | "completed"
    | "error"
    | "cancelled"
    | "restarted"
    | "idle";
    progress: number;
    errors: string[];
  };
  metadata?: Record<string, any>;
}
type ProcessFileResult =
  | {
    fileState: FileState;
    error?: never;
    file?: never;
  }
  | {
    error: string;
    fileState?: never;
    file?: never;
  };

interface UseUploadProps {
  action: string;
  globalConfig?: {
    maxConcurrentUploads?: number;
    maxFiles?: number;
    mode?: "single" | "multiple";
    maxFileSize?: number;
    allowedFileTypes?: string[];
    disabled?: boolean; // New disabled option
  };
  metadata?: Record<string, any>;
  onUploadStart?: (files: FileState[]) => void;
  onUploadComplete?: (data: any) => void; // Change the parameter type
  onError?: (error: Error, file: FileState) => void;
  headers?: Record<string, string>;
}

export const useUpload = ({
  action,
  globalConfig = {},
  metadata = {},
  onUploadStart,
  onUploadComplete,
  onError,
  headers,
}: UseUploadProps) => {
  //default settings for globalConfig
  const {
    mode = "multiple",
    maxFiles = 3,
    maxFileSize = 10 * 1024 * 1024 * 1024,
    allowedFileTypes = ["*/*"],
    maxConcurrentUploads = 5,
    disabled = false, // Add disabled with default false
  } = globalConfig;

  // State for tracking drag activity
  const [isDragActive, setIsDragActive] = useState(false);

  const [acceptedFiles, setAcceptedFiles] = useState<FileState[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const uploadQueue = useRef<string[]>([]);
  const activeUploads = useRef<Set<string>>(new Set());
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);
  // Helper function to generate preview URLs
  const generatePreviewUrl = useCallback(
    (file: File): Promise<{ imagePreview?: string; videoPreview?: string }> => {
      return new Promise((resolve, reject) => {
        const fileType = file.type || "";

        // Generalized file type check that considers both explicit types and wildcard types
        const isAllowedType = allowedFileTypes.some((type) => {
          if (type.endsWith("*")) {
            const typePrefix = type.slice(0, -1);
            return fileType.startsWith(typePrefix);
          }
          return fileType === type;
        });

        // Image preview generation
        if (
          isAllowedType &&
          (fileType.startsWith("image/") ||
            allowedFileTypes.includes("image/*"))
        ) {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              imagePreview: reader.result as string,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        }
        // Video preview generation
        else if (
          isAllowedType &&
          (allowedFileTypes.includes("video/*") ||
            allowedFileTypes.includes(fileType) ||
            allowedFileTypes.some(
              (type) => type.startsWith("video/") && type === fileType,
            ))
        ) {
          // Create video thumbnail
          const videoElement = document.createElement("video");
          videoElement.preload = "metadata";

          // Create video URL for playback
          const videoPreviewUrl = URL.createObjectURL(file);

          videoElement.onloadedmetadata = () => {
            const canvas = document.createElement("canvas");
            canvas.width = videoElement.videoWidth || 320;
            canvas.height = videoElement.videoHeight || 240;

            const ctx = canvas.getContext("2d");
            ctx?.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

            // Revoke the object URL to free up memory
            URL.revokeObjectURL(videoPreviewUrl);

            resolve({
              videoPreview: videoPreviewUrl,
            });
          };

          videoElement.onerror = reject;
          videoElement.src = videoPreviewUrl;
        } else {
          // For non-matching file types, return empty preview
          resolve({});
        }
      });
    },
    [allowedFileTypes],
  );

  // Validate file based on type and size
  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      // Check file type
      const isValidFileType = allowedFileTypes.some((type) => {
        if (type.endsWith("*")) {
          const typePrefix = type.slice(0, -1);
          return file.type.startsWith(typePrefix);
        }
        return file.type === type;
      });

      if (!isValidFileType) {
        return {
          valid: false,
          error: `Invalid file type: ${file.type}. Allowed types: ${allowedFileTypes.join(", ")}`,
        };
      }

      // Check file size
      if (file.size > maxFileSize) {
        return {
          valid: false,
          error: `File too large: ${file.name}. Max size: ${maxFileSize / (1024 * 1024)}MB`,
        };
      }

      // Check number of files in single/multiple mode
      if (mode === "single" && acceptedFiles.length >= 1) {
        return {
          valid: false,
          error: "Only one file allowed in single mode",
        };
      }

      if (mode === "multiple" && acceptedFiles.length >= maxFiles) {
        return {
          valid: false,
          error: `Maximum ${maxFiles} files allowed`,
        };
      }

      return { valid: true };
    },
    [allowedFileTypes, maxFileSize, mode, maxFiles, acceptedFiles],
  );

  // Fetch with progress tracking using Axios
  const fetchWithProgress = async (
    url: string,
    options: {
      method?: string;
      body: FormData | Blob;
      headers?: Record<string, string>;
      signal?: AbortSignal;
      startByte?: number;
      fileId: string; // Add fileId to track specific file
      onUploadProgress?: (progressEvent: any) => void;
    },
  ) => {
    const {
      method = "POST",
      body,
      headers = {
        "Content-Type": "multipart/form-data",
      },
      signal,
      startByte = 0,
      fileId,
      onUploadProgress,
    } = options;

    // Total file size calculation
    const totalBytes =
      body instanceof FormData
        ? Array.from(body.values()).reduce(
          (total, value) =>
            total +
            (value instanceof Blob ? value.size : new Blob([value]).size),
          0,
        )
        : (body as Blob).size;

    return axios({
      url,
      method,
      data: body,
      headers: { ...headers },
      signal: signal && signal.aborted ? signal : undefined,
      onUploadProgress: (progressEvent) => {
        const loaded = progressEvent.loaded + startByte;
        const progress = Math.min(Math.round((loaded / totalBytes) * 100), 100);

        // Update local state
        setAcceptedFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                ...f,
                state: { ...f.state, progress },
              }
              : f,
          ),
        );

        // Call the external progress callback
        onUploadProgress?.({
          loaded,
          total: totalBytes,
          progress,
        });
      },
    })
      .then((response) => response)
      .catch((error) => {
        if (axios.isCancel(error)) {
          throw new Error("Upload aborted");
        }
        throw error;
      });
  };

  // Modified uploadFile to work with queue
  const uploadFile = useCallback(
    async (fileId: string) => {
      // If disabled, prevent file upload
      if (disabled) return;

      const fileIndex = acceptedFiles.findIndex((f) => f.id === fileId);
      if (fileIndex === -1) return;

      const fileState = acceptedFiles[fileIndex];

      // Prevent restarting an already uploading or completed file
      if (["uploading", "completed"].includes(fileState.state.status)) return;

      const controller = new AbortController();
      abortControllers.current.set(fileId, controller);

      try {
        // Update file state to uploading
        setAcceptedFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                ...f,
                state: {
                  ...f.state,
                  status: "uploading",
                  progress: 0,
                  errors: [],
                },
              }
              : f,
          ),
        );

        // Prepare file for upload
        const formData = new FormData();
        formData.append("file", fileState.file);

        // Upload the file
        const data = await fetchWithProgress(action, {
          method: "POST",
          body: formData,
          headers: { ...headers },
          signal: controller.signal,
          fileId, // Pass the fileId
          onUploadProgress: (progress) => {
            // This is an additional mechanism to ensure progress is tracked
            setAcceptedFiles((prev) =>
              prev.map((f) =>
                f.id === fileId
                  ? {
                    ...f,
                    state: {
                      ...f.state,
                      progress: progress.progress,
                      status:
                        progress.progress === 100 ? "completed" : "uploading",
                    },
                  }
                  : f,
              ),
            );
          },
        });

        // Handle response
        if (typeof data === "object" && data !== null && "status" in data) {
          // If data contains status information, check it
          if (!data) {
            throw new Error(`Upload failed with status: ${data}`);
          }
        } else if (typeof data === "string") {
          // Handle non-JSON responses if necessary
          throw new Error("Unexpected response format");
        }

        // Trigger completion callback
        onUploadComplete?.(data.data); // Pass the data instead of response

        return data;
      } catch (error) {
        // Handle upload errors
        setAcceptedFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? {
                ...f,
                state: {
                  ...f.state,
                  status: "error",
                  progress: 0, // Reset progress to 0 on error
                  errors: [(error as Error).message || "Upload failed"],
                },
              }
              : f,
          ),
        );

        onError?.(error as Error, fileState);
        throw error;
      } finally {
        abortControllers.current.delete(fileId);
      }
    },
    [acceptedFiles, action, onUploadComplete, onError, fetchWithProgress, disabled],
  );

  // Improved processUploadQueue
  const processUploadQueue = useCallback(async () => {
    if (mode === "single") {
      // Do not process the queue in single mode automatically
      return;
    }
    // Continue processing while we have files in the queue
    while (uploadQueue.current.length > 0) {
      // Find files that are pending and not currently uploading
      const pendingFiles = uploadQueue.current.filter((fileId) => {
        const file = acceptedFiles.find((f) => f.id === fileId);
        return (
          file?.state.status === "pending" && !activeUploads.current.has(fileId)
        );
      });

      // Stop processing if no pending files
      if (pendingFiles.length === 0) break;

      // Determine how many more files we can upload concurrently
      const availableSlots = maxConcurrentUploads - activeUploads.current.size;

      // If no available slots, wait a bit before checking again
      if (availableSlots <= 0) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        continue;
      }

      // Select files to upload based on available slots
      const filesToUpload = pendingFiles.slice(0, availableSlots);

      // Upload files concurrently without waiting for all to complete
      filesToUpload.forEach(async (fileId) => {
        // Prevent multiple uploads of the same file
        if (activeUploads.current.has(fileId)) return;

        // Mark file as active
        activeUploads.current.add(fileId);

        try {
          await uploadFile(fileId);
        } catch (error) {
          // Error handling is done in uploadFile method
        } finally {
          // Remove from active uploads
          activeUploads.current.delete(fileId);

          // Remove from upload queue if completed or errored
          uploadQueue.current = uploadQueue.current.filter(
            (id) => id !== fileId,
          );
        }
      });

      // Small delay to prevent overwhelming the system
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }, [uploadFile, maxConcurrentUploads, acceptedFiles]);

  // Modify processFiles to add stricter file selection limit
  const processFiles = useCallback(
    async (files: File[]) => {
      setIsValidatingFiles(true);
      try {
        // Helper function to generate a unique file key
        const generateFileKey = (file: File) =>
          `${file.name}-${file.size}-${file.lastModified}`;

        // Remove duplicates using the unique file key
        const uniqueFiles = Array.from(
          new Map(files.map((file) => [generateFileKey(file), file])).values(),
        );

        // Check for duplicate files compared to already accepted files
        const existingFileKeys = new Set(
          acceptedFiles.map((f) => generateFileKey(f.file)),
        );

        const newUniqueFiles = uniqueFiles.filter(
          (file) => !existingFileKeys.has(generateFileKey(file)),
        );

        if (mode === "single") {
          // In single mode, only process the first unique file
          if (newUniqueFiles.length > 1) {
            setErrors((prev) => [
              ...prev,
              `Only one file can be selected in single mode.`,
            ]);
            return;
          }

          // Abort any ongoing upload for the previous file
          if (acceptedFiles.length > 0) {
            const previousFileId = acceptedFiles[0].id;
            const controller = abortControllers.current.get(previousFileId);
            if (controller) {
              controller.abort();
              abortControllers.current.delete(previousFileId);
            }
          }

          // If no unique files, return early
          if (newUniqueFiles.length === 0) return;

          const fileToProcess = newUniqueFiles[0];

          // Generate preview URL for the file
          const { imagePreview, videoPreview } =
            await generatePreviewUrl(fileToProcess);

          // Replace the existing file without uploading automatically
          setAcceptedFiles([
            {
              id: uuidv4(),
              file: fileToProcess,
              previewUrl: imagePreview,
              videoPreviewUrl: videoPreview,
              state: {
                status: "idle",
                progress: 0,
                errors: [],
              },
              metadata: { ...metadata },
            },
          ]);
        } else {
          // Multiple mode processing
          // Check if adding these files would exceed maxFiles
          const totalFilesAfterAddition =
            acceptedFiles.length + newUniqueFiles.length;

          if (totalFilesAfterAddition > maxFiles) {
            // Slice the files to only include what can be added within the maxFiles limit
            const limitedFiles = newUniqueFiles.slice(
              0,
              maxFiles - acceptedFiles.length,
            );

            // Add an error if user tried to select more files than allowed
            setErrors((prev) => [
              ...prev,
              `Maximum ${maxFiles} files allowed. Only ${limitedFiles.length} files were added.`,
            ]);

            // Update newUniqueFiles to the limited set
            newUniqueFiles.splice(0, limitedFiles.length);
          }

          // Validate and process each file
          const processedFiles = await Promise.all(
            newUniqueFiles.map(async (file): Promise<ProcessFileResult> => {
              // Generate preview
              const { imagePreview, videoPreview } =
                await generatePreviewUrl(file);

              // Validate file
              const validationResult = validateFile(file);

              return validationResult.valid
                ? {
                  fileState: {
                    id: uuidv4(),
                    file,
                    previewUrl: imagePreview,
                    videoPreviewUrl: videoPreview,
                    state: {
                      status: "pending",
                      progress: 0,
                      errors: [],
                    },
                    metadata: { ...metadata },
                  },
                }
                : {
                  error: validationResult.error || "Validation failed",
                };
            }),
          );

          // Separate valid and invalid files
          const invalidFileErrors = processedFiles
            .filter(
              (result): result is { error: string } =>
                result.error !== undefined,
            )
            .map((result) => result.error);

          const validFilesWithState = processedFiles
            .filter(
              (result): result is { fileState: FileState } =>
                result.fileState !== undefined,
            )
            .map((result) => result.fileState);

          // Update errors if any
          if (invalidFileErrors.length > 0) {
            setErrors((prev) => [...prev, ...invalidFileErrors]);
          }

          // Add new files to queue
          setAcceptedFiles((prev) => {
            // Add file IDs to upload queue
            uploadQueue.current = [
              ...uploadQueue.current,
              ...validFilesWithState.map((file) => file.id),
            ];

            // Trigger upload start callback
            onUploadStart?.(validFilesWithState);

            // Start processing queue
            processUploadQueue();

            return [...prev, ...validFilesWithState];
          });
        }
      } finally {
        // Set validation state to false when processing is complete
        setIsValidatingFiles(false);
      }
    },
    [
      validateFile,
      metadata,
      onUploadStart,
      processUploadQueue,
      acceptedFiles,
      mode,
      maxFiles,
      generatePreviewUrl,
    ],
  );

  // Remove a file
  const removeFile = useCallback((fileId: string) => {
    const controller = abortControllers.current.get(fileId);

    // Abort ongoing upload if exists
    if (controller) {
      try {
        controller.abort();
      } catch (error) {
        console.warn(`Error aborting upload for file ${fileId}:`, error);
      }
      abortControllers.current.delete(fileId);
    }

    // Remove file from state
    setAcceptedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  // Restart upload
  const restartUpload = useCallback(
    (fileId: string) => {
      setAcceptedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
              ...f,
              state: {
                ...f.state,
                status: "pending",
                progress: 0,
                errors: [],
              },
            }
            : f,
        ),
      );
      uploadFile(fileId);
    },
    [uploadFile],
  );

  // Modify getRootProps to handle disabled state
  const getRootProps = useCallback(
    (): getRootFileZoneProps => {
      if (disabled) {
        return {
          onDragEnter: (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
          },
          onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
          },
          onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
          },
          onDrop: (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
          },
          onClick: () => {
            // Do nothing when disabled
            return;
          },
        };
      }

      // Original implementation remains the same
      return {
        onDragEnter: (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragActive(true);
        },
        onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragActive(false);
        },
        onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          if (!isDragActive) {
            setIsDragActive(true);
          }
        },
        onDrop: (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragActive(false);

          const droppedFiles = Array.from(event.dataTransfer.files);

          if (
            mode === "multiple" &&
            droppedFiles.length + acceptedFiles.length > maxFiles
          ) {
            const limitedFiles = droppedFiles.slice(
              0,
              maxFiles - acceptedFiles.length,
            );
            processFiles(limitedFiles);

            setErrors((prev) => [
              ...prev,
              `Maximum ${maxFiles} files allowed. Only ${limitedFiles.length} files were added.`,
            ]);
          } else {
            processFiles(droppedFiles);
          }
        },
        onClick: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.multiple = mode === "multiple";
          input.max = mode === "multiple" ? maxFiles.toString() : "1";
          input.accept = allowedFileTypes.join(",");
          input.onchange = (e) => {
            const target = e.target as HTMLInputElement;
            if (target.files) {
              const filesArray = Array.from(target.files);

              if (mode === "multiple" && filesArray.length > maxFiles) {
                const limitedFiles = filesArray.slice(0, maxFiles);
                processFiles(limitedFiles);

                setErrors((prev) => [
                  ...prev,
                  `Maximum ${maxFiles} files allowed. Only ${limitedFiles.length} files were added.`,
                ]);
              } else {
                processFiles(filesArray);
              }
            }
          };
          input.click();
        },
      };
    },
    [
      processFiles,
      mode,
      allowedFileTypes,
      maxFiles,
      acceptedFiles,
      isDragActive,
      disabled, // Add disabled to dependencies
    ],
  );

  // Modify getInputProps to handle disabled state
  const getInputProps = useCallback(
    (): getInputFileZoneProps => {
      if (disabled) {
        return {
          type: "file",
          multiple: false,
          accept: "",
          max: "0",
          onChange: () => { },
        };
      }

      // Original implementation remains the same
      return {
        type: "file",
        multiple: mode === "multiple",
        accept: allowedFileTypes.join(","),
        max: mode === "multiple" ? maxFiles.toString() : "1",
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const target = e.target as HTMLInputElement;
          if (target.files) {
            const filesArray = Array.from(target.files);

            if (mode === "multiple" && filesArray.length > maxFiles) {
              const limitedFiles = filesArray.slice(0, maxFiles);
              processFiles(limitedFiles);

              setErrors((prev) => [
                ...prev,
                `Maximum ${maxFiles} files allowed. Only ${limitedFiles.length} files were added.`,
              ]);
            } else {
              processFiles(filesArray);
            }
          }
        },
      };
    },
    [processFiles, mode, allowedFileTypes, maxFiles, disabled], // Add disabled to dependencies
  );
  // Additional useEffect to process queue when files change
  useEffect(() => {
    if (mode === "multiple" && uploadQueue.current.length > 0) {
      processUploadQueue();
    }
  }, [mode, processUploadQueue, acceptedFiles]);

  return {
    acceptedFiles,
    errors,
    getRootProps,
    getInputProps,
    removeFile,
    uploadFile,
    isDragActive,
    isValidatingFiles,
    restartUpload,
    disabled,
  };
};
